import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import {
  TAB_BUTTON_DESIGN_HEIGHT,
  TAB_BUTTON_DESIGN_WIDTH,
  TAB_BUTTON_OUTLINED_INNER_PATH_D,
  TAB_BUTTON_OUTLINED_OUTER_PATH_D,
  TAB_BUTTON_SLICE_LEFT,
  TAB_BUTTON_SLICE_RIGHT,
  getTabButtonDisplayPixelWidth,
  getTabButtonEffectiveWidth,
  getTabButtonLabelGroupTransform,
  getTabButtonNineSliceMiddleScale,
  getTabButtonNineSliceMinWidth,
  tabButtonSvgAttrs,
} from '../../utils/resolveTabButtonSvgSize'
import { useTabButtonSvgClipPathId } from '../../utils/useTabButtonSvgClipPathId'
import { TabButtonContainer } from './component.styles'

function TabButtonOutlined({
  label,
  onClick,
  onKeyPress,
  ultraWide,
  wide,
  width,
}) {
  const effectiveWidth = getTabButtonEffectiveWidth(width, wide, ultraWide)
  const clipId = useTabButtonSvgClipPathId()
  const wPx = getTabButtonDisplayPixelWidth(effectiveWidth)

  const renderOutlinedPaths = vectorEffect => (
    <>
      <path
        className="tab-button-outlined-outer"
        d={TAB_BUTTON_OUTLINED_OUTER_PATH_D}
        fill="currentColor"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        vectorEffect={vectorEffect}
      />
      <path
        className="tab-button-outlined-inner"
        d={TAB_BUTTON_OUTLINED_INNER_PATH_D}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        vectorEffect={vectorEffect}
      />
    </>
  )

  let svgNode = null
  if (wPx != null) {
    const w = Math.max(getTabButtonNineSliceMinWidth(), wPx)
    const sx = getTabButtonNineSliceMiddleScale(w)
    svgNode = (
      <svg
        aria-hidden
        height={TAB_BUTTON_DESIGN_HEIGHT}
        viewBox={`0 0 ${w} ${TAB_BUTTON_DESIGN_HEIGHT}`}
        width={w}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <clipPath id={`${clipId}-l`}>
            <rect
              height={TAB_BUTTON_DESIGN_HEIGHT}
              width={TAB_BUTTON_SLICE_LEFT}
              x={0}
              y={0}
            />
          </clipPath>
          <clipPath id={`${clipId}-m`}>
            <rect
              height={TAB_BUTTON_DESIGN_HEIGHT}
              width={TAB_BUTTON_SLICE_RIGHT - TAB_BUTTON_SLICE_LEFT}
              x={TAB_BUTTON_SLICE_LEFT}
              y={0}
            />
          </clipPath>
          <clipPath id={`${clipId}-r`}>
            <rect
              height={TAB_BUTTON_DESIGN_HEIGHT}
              width={TAB_BUTTON_DESIGN_WIDTH - TAB_BUTTON_SLICE_RIGHT}
              x={TAB_BUTTON_SLICE_RIGHT}
              y={0}
            />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId}-l)`}>{renderOutlinedPaths(undefined)}</g>
        <g
          clipPath={`url(#${clipId}-m)`}
          transform={`translate(${TAB_BUTTON_SLICE_LEFT},0) scale(${sx},1) translate(${-TAB_BUTTON_SLICE_LEFT},0)`}
        >
          {renderOutlinedPaths('nonScalingStroke')}
        </g>
        <g
          clipPath={`url(#${clipId}-r)`}
          transform={`translate(${w - TAB_BUTTON_DESIGN_WIDTH},0)`}
        >
          {renderOutlinedPaths(undefined)}
        </g>
        <g>
          <text dominantBaseline="middle" textAnchor="middle" x="50%" y="53%">
            {label}
          </text>
        </g>
      </svg>
    )
  } else {
    svgNode = (
      <svg
        {...tabButtonSvgAttrs(effectiveWidth, 'default')}
        aria-hidden
        enableBackground="new 0 0 159 41"
        version="1.1"
        viewBox={`0 0 ${TAB_BUTTON_DESIGN_WIDTH} ${TAB_BUTTON_DESIGN_HEIGHT}`}
        xmlSpace="preserve"
        xmlns="http://www.w3.org/2000/svg"
      >
        {renderOutlinedPaths(undefined)}
        <g
          transform={getTabButtonLabelGroupTransform(effectiveWidth, 'default')}
        >
          <text dominantBaseline="middle" textAnchor="middle" x="50%" y="53%">
            {label}
          </text>
        </g>
      </svg>
    )
  }

  return (
    <Fragment key={`tab-button-outlined-${label}`}>
      <TabButtonContainer
        aria-label={label}
        dataTestId="k8-admin-dashboard-tab-button"
        onClick={onClick}
        onKeyPress={onKeyPress}
        role="tab"
        tabIndex={0}
      />
      {svgNode}
    </Fragment>
  )
}

TabButtonOutlined.propTypes = {
  label: PropTypes.string,
  onClick: PropTypes.func,
  onKeyPress: PropTypes.func,
  ultraWide: PropTypes.bool,
  wide: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export default TabButtonOutlined
